// src/components/Header/QuickLinksMenu.tsx
import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  CheckSquare,
  Users,
  BarChart3,
  Calendar,
  Clock,
  GitBranch,
  Rocket,
  Settings
} from 'lucide-react';
import './Modals.scss';

interface QuickLinksMenuProps {
  isOpen: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
}

export const QuickLinksMenu: React.FC<QuickLinksMenuProps> = ({ isOpen, onClose, anchorEl }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [position, setPosition] = React.useState({ top: 0, right: 0 });

  useEffect(() => {
    if (isOpen && anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  }, [isOpen, anchorEl]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) &&
          anchorEl && !anchorEl.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, anchorEl]);

  if (!isOpen) return null;

  const quickLinks = [
    { icon: <LayoutDashboard size={20} />, label: 'Dashboard', path: '/dashboard' },
    { icon: <CheckSquare size={20} />, label: 'My Work', path: '/my-work' },
    { icon: <GitBranch size={20} />, label: 'Sprint Board', path: '/sprint-board' },
    { icon: <Rocket size={20} />, label: 'Sprint Dashboard', path: '/sprint-dashboard' },
    { icon: <Users size={20} />, label: 'Team', path: '/team' },
    { icon: <BarChart3 size={20} />, label: 'Analytics', path: '/analytics' },
    { icon: <Calendar size={20} />, label: 'Calendar', path: '/calendar' },
    { icon: <Clock size={20} />, label: 'Time Tracking', path: '/time-tracking' },
    { icon: <Settings size={20} />, label: 'Settings', path: '/settings' },
  ];

  const handleLinkClick = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <div ref={menuRef} className="quick-links-menu" style={{ top: `${position.top}px`, right: `${position.right}px` }}>
      <div className="quick-links-header">
        <h3>Quick Links</h3>
      </div>
      <div className="quick-links-grid">
        {quickLinks.map((link, index) => (
          <button
            key={index}
            className="quick-link-item"
            onClick={() => handleLinkClick(link.path)}
          >
            <div className="quick-link-icon">{link.icon}</div>
            <span className="quick-link-label">{link.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};
